import React, { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { useAccount } from '../context/AccountContext';
import { useFavorites } from '../context/FavoritesContext';
import { toast } from 'react-toastify';
import styles from '../styles/ProductList.module.css';
import SellerBadge from './SellerBadge';
import SearchAutocomplete from './SearchAutocomplete';
import SensitiveThumbnail from './SensitiveThumbnail';
import { deals } from '../data/deals';
import { categories as catalogCategories, findCategory, slugify } from '../data/catalog';
import { fetchJsonWithBusyRetry } from '../utils/fetchJsonWithBusyRetry';
import { dealsRequestCache, facetsRequestCache } from '../utils/requestCache';
import { withShopifyWidth } from '../utils/shopifyImage';
import { buildAlertCtaUrl } from '../utils/alertCta';
import { trackOutboundClick, trackSearchEvent } from '../lib/analyticsTracking';
import { getExistingSubscription, isPushSupported, subscribeToPush } from '../lib/pushNotifications';
import telegramIcon from '../assets/icons/telegram-icon.webp';
import { AMAZON_PROMOS } from '../data/amazonPromos';
import AmazonPromoIcon from './AmazonPromoIcon';

const TELEGRAM_URL = 'https://t.me/esganga';

const categoryOptions = [{ slug: '', name: 'Todas las categorias' }, ...catalogCategories];
const DEFAULT_PAGE_SIZE = 50;
const MAX_DUPLICATE_PAGE_SKIPS = 3;
// Busqueda corregida por el servidor (cabecera X-Esganga-Search-Corrected)
// por URL pedida, para que una respuesta servida desde dealsRequestCache
// muestre el mismo aviso que la original.
const searchCorrectionsByUrl = new Map();
const MAX_SEARCH_CORRECTIONS = 100;

function readSearchCorrection(response) {
    const raw = response?.headers?.get?.('X-Esganga-Search-Corrected');
    if (!raw) return '';
    try {
        return decodeURIComponent(raw).slice(0, 120);
    } catch (error) {
        return '';
    }
}

function rememberSearchCorrection(url, correction) {
    searchCorrectionsByUrl.delete(url);
    searchCorrectionsByUrl.set(url, correction);
    while (searchCorrectionsByUrl.size > MAX_SEARCH_CORRECTIONS) {
        searchCorrectionsByUrl.delete(searchCorrectionsByUrl.keys().next().value);
    }
}
const travelQuickFilters = [
    { value: '', label: 'Todos los viajes' },
    { value: 'vuelo', label: 'Vuelos' },
    { value: 'hotel', label: 'Hoteles' },
    { value: 'escapada', label: 'Escapadas' },
    { value: 'paquete', label: 'Vuelo + hotel' },
    { value: 'coche', label: 'Coches' }
];

const ProductList = ({
    initialCategory = 'Todos',
    initialStore = '',
    initialStoreSlug = '',
    initialSearch = '',
    initialMaxPrice = '',
    eyebrow = 'Gangas verificadas hoy',
    title = 'Ofertas destacadas para ahorrar sin dar vueltas',
    description = 'Revisa precio, descuento, tienda y detalles antes de abrir la oferta en la tienda.',
    initialSort = 'recent',
    showFilters = true,
    showControls = true,
    inlineFeatured = false,
    inlineFeaturedExcludeIds = [],
    // Que la PRIMERA tarjeta del listado sea la imagen LCP de la pagina. Es el
    // valor por defecto porque en los listados puros (/buscar, /chollos/*,
    // /tiendas/*) la foto que mas arriba se ve es la primera. La home NO lo
    // cumple: su LCP es la foto del carrusel grande de arriba
    // (Home.js, `boardImage`), asi que ahi hay que apagarlo o se acumulan dos
    // `fetchpriority="high"` en la misma pagina.
    priorityFirstImage = true,
    pageSize = DEFAULT_PAGE_SIZE,
    autoLoadMore = true,
    browseMoreHref = '',
    browseMoreLabel = 'Ver todas las ofertas'
}) => {
    const { addToCart } = useContext(CartContext);
    const { user } = useAccount();
    const { favoriteIds, toggleFavorite } = useFavorites();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeCategorySlug, setActiveCategorySlug] = useState(getCategorySlug(initialCategory));
    const [apiDeals, setApiDeals] = useState(null);
    const [apiFailed, setApiFailed] = useState(false);
    const [searchTerm, setSearchTerm] = useState(initialSearch);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
    const [correctedSearch, setCorrectedSearch] = useState('');
    const [sortBy, setSortBy] = useState(initialSort);
    const [minDiscount, setMinDiscount] = useState('0');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState(initialMaxPrice);
    const [onlyHot, setOnlyHot] = useState(false);
    const [selectedProductType, setSelectedProductType] = useState('');
    const [selectedAttributes, setSelectedAttributes] = useState({});
    const [facetData, setFacetData] = useState(null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(() => Boolean(location.state?.keepFiltersOpen));
    const [reloadToken, setReloadToken] = useState(0);
    const [comparedDeals, setComparedDeals] = useState([]);
    const [inlineFeaturedDeals, setInlineFeaturedDeals] = useState([]);
    const loadMoreRef = useRef(null);
    // Sincronizado en cada render justo antes del efecto que crea el
    // observer (ver el comentario extenso ahi) -- los valores iniciales
    // aqui no importan, se sobrescriben siempre antes de usarse.
    const loadMoreStateRef = useRef({ hasMore: true, isLoading: true, hasItems: false });
    const advancedFiltersId = useId();
    // Recuerda para que filtersKey ya se auto-reintento, para no entrar en
    // bucle si el fallo es real/persistente -- el boton "Reintentar" manual
    // sigue disponible para eso.
    const autoRetriedKeyRef = useRef(null);
    // BUG REAL reportado por el usuario (2026-09-24): "siempre veo el mismo
    // producto" y el scroll infinito deja de traer nada nuevo mucho antes de
    // agotar el catalogo real. Causa raiz: en la home (y en cualquier listado
    // grande), la pagina que responde "cargar mas" no es una foto fija -- la
    // mezcla de home rota cada ciclo del scheduler (refreshStoreDiversityBuckets)
    // y el propio /api/deals limita el offset maximo (proteccion real de coste
    // de query) -- asi que una pagina pedida a mitad de scroll puede llegar
    // llena (mismo `pageSize` de siempre) pero con ids que YA se habian visto
    // (la mezcla se reordeno debajo, o el offset se topo con el limite y
    // repite la ultima pagina real). Antes `hasMore` solo miraba si la
    // respuesta traia `pageSize` elementos, sin comprobar si aportaban algo
    // nuevo -- una pagina 100% duplicada se contaba como "hay mas", pero
    // `mergeDeals` la descartaba entera: la lista visible dejaba de crecer
    // para siempre mientras el indicador de carga seguia disparandose (o ni
    // eso, si el centinela no volvia a cruzar el umbral). Fix: se avanza
    // automaticamente a la siguiente pagina cuando una pagina no vacia no
    // aporta ningun id nuevo (probablemente un hueco pasajero de la rotacion),
    // acotado a un numero pequeno de intentos seguidos para no encadenar
    // peticiones sin fin si de verdad no queda nada nuevo. El criterio es "no
    // vacia", nunca "llena": el colapso de variantes del servidor vuelve
    // legitimas las paginas cortas (ver el comentario en applyDeals).
    const duplicatePageStreakRef = useRef(0);
    // Dos tiendas distintas pueden compartir el mismo nombre visible (p.ej. dos
    // altas de "ADS Running Shop" con slugs reales distintos) -- slugify(name)
    // siempre colapsa en el mismo resultado y filtraria por la tienda
    // equivocada. Si DealLanding nos pasa el slug real (initialStoreSlug), se
    // usa tal cual; solo se recae en slugify(initialStore) cuando no hay slug
    // (dataset de fallback local, que no tiene slugs reales).
    const storeSlugParam = initialStoreSlug || (initialStore ? slugify(initialStore) : '');

    useEffect(() => {
        setActiveCategorySlug(getCategorySlug(initialCategory));
    }, [initialCategory]);

    useEffect(() => {
        setSearchTerm(initialSearch);
        setDebouncedSearchTerm(initialSearch);
    }, [initialSearch]);

    useEffect(() => {
        setMaxPrice(initialMaxPrice);
    }, [initialMaxPrice]);

    useEffect(() => {
        setSortBy(initialSort);
    }, [initialSort]);

    // Espera a que el usuario deje de teclear antes de disparar la busqueda
    // real (fetch a /api/deals + evento de analitica) -- antes cada tecla
    // lanzaba su propio fetch sin cancelar el anterior.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        setSelectedProductType('');
        setSelectedAttributes({});
    }, [activeCategorySlug, storeSlugParam, debouncedSearchTerm]);

    // Firma de los filtros (todo menos `page`). Cuando cambia, es una busqueda
    // nueva y hay que volver a la pagina 0 antes de pedir nada; cuando no
    // cambia, un cambio de `page` es "cargar mas" sobre la busqueda actual.
    // Antes esto vivia en dos useEffect separados (uno reseteaba `page` a 0,
    // otro hacia el fetch con `page` en sus dependencias) y podian dispararse
    // en el mismo ciclo con el `page` todavia desactualizado, disparando un
    // fetch con el offset viejo que a veces llegaba despues del correcto y
    // reinsertaba resultados obsoletos. Un unico efecto con esta guarda evita
    // la condicion de carrera.
    const filtersKey = useMemo(() => JSON.stringify([
        activeCategorySlug, storeSlugParam, minDiscount, minPrice, maxPrice,
        onlyHot, debouncedSearchTerm, sortBy, selectedProductType, selectedAttributes, pageSize
    ]), [activeCategorySlug, storeSlugParam, minDiscount, minPrice, maxPrice, onlyHot, debouncedSearchTerm, sortBy, selectedProductType, selectedAttributes, pageSize]);
    const filtersKeyRef = useRef(filtersKey);

    useEffect(() => {
        const isFilterChange = filtersKeyRef.current !== filtersKey;

        if (isFilterChange) {
            filtersKeyRef.current = filtersKey;
            // No se vacia apiDeals aqui: hacerlo dejaba la pantalla en blanco
            // (skeletons) durante el refresco entero de cada cambio de filtro,
            // un vacio brusco de un par de segundos. El listado anterior se
            // queda visible (con un indicador de carga sutil) hasta que
            // .then() lo sustituye por los resultados nuevos.
            setApiFailed(false);
            setHasMore(true);
            duplicatePageStreakRef.current = 0;

            if (page !== 0) {
                // Este efecto se relanzara solo cuando `page` llegue a 0.
                setPage(0);
                return undefined;
            }
        }

        let active = true;
        const controller = new AbortController();
        const params = new URLSearchParams({
            limit: String(pageSize),
            offset: String(page * pageSize)
        });

        if (activeCategorySlug) {
            params.set('category', activeCategorySlug);
        }

        if (storeSlugParam) {
            params.set('store', storeSlugParam);
        }

        if (debouncedSearchTerm.trim()) {
            params.set('q', debouncedSearchTerm.trim());
        }

        if (Number(minDiscount) > 0) {
            params.set('minDiscount', minDiscount);
        }

        if (Number(minPrice) > 0) {
            params.set('minPrice', minPrice);
        }

        if (Number(maxPrice) > 0) {
            params.set('maxPrice', maxPrice);
        }

        if (onlyHot) {
            params.set('hot', 'true');
        }

        if (selectedProductType) {
            params.set('productType', selectedProductType);
        }

        Object.entries(selectedAttributes).forEach(([key, value]) => {
            if (value) {
                params.set(`attr_${key}`, value);
            }
        });

        params.set('sort', sortBy);
        const requestUrl = `/api/deals?${params.toString()}`;

        const applyDeals = (nextDeals) => {
            const baseDeals = page === 0 ? [] : (apiDeals || []);
            const merged = page === 0 ? nextDeals : mergeDeals(baseDeals, nextDeals);
            const newItemsCount = merged.length - baseDeals.length;

            setApiDeals(merged);
            setApiFailed(false);

            // Ver el comentario junto a duplicatePageStreakRef: una pagina no
            // vacia que no aporta ningun id nuevo no es "se acabo el
            // catalogo", es casi siempre un hueco pasajero (rotacion de la
            // mezcla de home, o el tope de offset repitiendo la ultima pagina
            // real) -- se reintenta con la siguiente pagina automaticamente,
            // acotado, antes de rendirse de verdad.
            if (page > 0 && nextDeals.length > 0 && newItemsCount === 0 && duplicatePageStreakRef.current < MAX_DUPLICATE_PAGE_SKIPS) {
                duplicatePageStreakRef.current += 1;
                setHasMore(true);
                setPage((currentPage) => currentPage + 1);
            } else {
                duplicatePageStreakRef.current = 0;
                // La UNICA senal fiable de que se acabo el catalogo es que el
                // servidor devuelva una pagina vacia. Antes se usaba
                // `nextDeals.length === pageSize`, y eso miente: groupDealVariants()
                // colapsa en una sola tarjeta las tallas del mismo producto
                // DESPUES de aplicar el LIMIT de SQL, asi que una pagina llena
                // en la base de datos puede llegar con menos tarjetas que
                // pageSize. Bug real reportado el 2026-10-01: en la home
                // (pageSize=12) llegaban 11 por un cardigan de La Redoute a
                // doble talla y se ensegnaba "Has visto todas las ofertas
                // disponibles con estos filtros" con 86.675 ofertas de Hogar
                // sin habermirado. Pedir una pagina de mas en el limite real
                // del catalogo no cuesta nada (vuelve vacia y para aqui).
                setHasMore(nextDeals.length > 0 && newItemsCount > 0);
            }

            if (page === 0) {
                setCorrectedSearch(debouncedSearchTerm.trim() ? (searchCorrectionsByUrl.get(requestUrl) || '') : '');
                recordSearchEvent({
                    query: debouncedSearchTerm.trim(),
                    category: activeCategorySlug,
                    store: storeSlugParam,
                    sort: sortBy,
                    minDiscount,
                    minPrice,
                    maxPrice,
                    onlyHot,
                    resultsCount: nextDeals.length
                });
            }
        };

        // Cache en memoria compartida entre montajes (utils/requestCache.js):
        // si esta combinacion exacta de filtros+pagina ya se pidio hace poco
        // (toggle de un filtro ya visto, volver atras, o un enlace que la
        // prefetcheo al pasar el raton), se sirve al instante sin red ni
        // estado de carga -- el "casi instantaneo" real, no solo percibido.
        const cachedDeals = dealsRequestCache.get(requestUrl);
        if (cachedDeals) {
            applyDeals(cachedDeals);
            setIsLoading(false);
            return undefined;
        }

        setIsLoading(true);

        fetchJsonWithBusyRetry(requestUrl, {
            signal: controller.signal,
            onResponse: (response) => rememberSearchCorrection(requestUrl, readSearchCorrection(response))
        })
            .then((data) => {
                if (active) {
                    const nextDeals = Array.isArray(data) ? data : [];
                    dealsRequestCache.set(requestUrl, nextDeals);
                    applyDeals(nextDeals);
                }
            })
            .catch((error) => {
                if (error.name === 'AbortError') return;
                if (active) {
                    setApiDeals((currentDeals) => Array.isArray(currentDeals) && currentDeals.length > 0 ? currentDeals : null);
                    setApiFailed(true);
                    setHasMore(false);
                }
            })
            .finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });

        return () => {
            active = false;
            controller.abort();
        };
        // Deliberadamente solo depende de `filtersKey` (resumen estable de todos
        // los filtros), `page` y el reintento manual: usar las variables sueltas
        // en vez de la clave rompería la guarda anti condicion-de-carrera.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey, page, reloadToken]);

    // Reporte real de usuario (2026-08-14): una busqueda ("Xiaomi") mostro
    // "sin resultados"/fallo y, tras esperar, los resultados aparecieron
    // solos -- sin que el usuario tocara nada. Investigado: /api/deals ya
    // reintenta una vez sola dentro de fetchJsonWithBusyRetry ante un 503 de
    // "servidor ocupado" (el admisor de lecturas publicas, 4 activas + cola
    // de 8), pero un pico de trafico sostenido mas largo que ese unico
    // reintento dejaba `apiFailed=true` esperando un clic manual en
    // "Reintentar" -- nada volvia a pedir los datos solo. Un reintento
    // automatico (una vez por busqueda/filtro, el boton manual sigue si este
    // tambien falla) cubre justo ese hueco sin arriesgar un bucle infinito
    // contra un fallo real y persistente.
    useEffect(() => {
        if (!apiFailed || autoRetriedKeyRef.current === filtersKey) {
            return undefined;
        }
        autoRetriedKeyRef.current = filtersKey;
        const timer = setTimeout(() => {
            setApiFailed(false);
            setIsLoading(true);
            setReloadToken((value) => value + 1);
        }, 2500);
        return () => clearTimeout(timer);
    }, [apiFailed, filtersKey]);

    useEffect(() => {
        let active = true;
        const controller = new AbortController();
        const params = new URLSearchParams();

        if (activeCategorySlug) params.set('category', activeCategorySlug);
        if (storeSlugParam) params.set('store', storeSlugParam);
        if (debouncedSearchTerm.trim()) params.set('q', debouncedSearchTerm.trim());
        if (Number(minDiscount) > 0) params.set('minDiscount', minDiscount);
        if (Number(minPrice) > 0) params.set('minPrice', minPrice);
        if (Number(maxPrice) > 0) params.set('maxPrice', maxPrice);
        if (onlyHot) params.set('hot', 'true');
        if (selectedProductType) params.set('productType', selectedProductType);
        Object.entries(selectedAttributes).forEach(([key, value]) => {
            if (String(value || '').trim()) params.set(`attr_${key}`, value);
        });

        const requestUrl = `/api/deals/facets?${params.toString()}`;
        const cachedFacets = facetsRequestCache.get(requestUrl);
        if (cachedFacets) {
            setFacetData(cachedFacets);
            return undefined;
        }

        fetchJsonWithBusyRetry(requestUrl, { signal: controller.signal })
            .then((data) => {
                if (active) {
                    facetsRequestCache.set(requestUrl, data);
                    setFacetData(data);
                }
            })
            .catch((error) => {
                if (error.name === 'AbortError') return;
                if (active) {
                    setFacetData(null);
                }
            });

        return () => {
            active = false;
            controller.abort();
        };
    }, [activeCategorySlug, storeSlugParam, minDiscount, minPrice, maxPrice, onlyHot, debouncedSearchTerm, selectedProductType, selectedAttributes]);

    // apiDeals empieza en null (todavia no sabemos si la API respondio bien o
    // mal): mientras tanto no mostramos nada, nunca los datos de ejemplo
    // (antes se veian productos de mentira un instante en cada carga porque
    // el fallback estaba ligado al mismo estado que el "cargando"). Los datos
    // de ejemplo solo aparecen si la API realmente fallo (apiFailed).
    const useDevelopmentFallback = apiFailed && process.env.NODE_ENV !== 'production';
    const hasApiResponse = apiDeals !== null;
    const sourceDeals = useMemo(
        () => (useDevelopmentFallback ? deals : (apiDeals || [])),
        [useDevelopmentFallback, apiDeals]
    );

    const visibleDeals = useMemo(() => {
        const filteredDeals = sourceDeals.filter((deal) => {
            const categoryMatches = !activeCategorySlug || slugify(deal.category) === activeCategorySlug;
            const storeMatches = !initialStore || slugify(deal.store) === slugify(initialStore);
            // La busqueda por texto ya la resuelve el servidor (stemming,
            // tildes, erratas y sinonimos con el motor de busqueda). Repetirla
            // aqui como "la frase exacta aparece seguida" descartaba resultados
            // buenos: "gafas sol" ocultaba "Gafas polarizadas de sol". Solo se
            // filtra en cliente el catalogo de ejemplo de desarrollo.
            const searchMatches = !useDevelopmentFallback || !searchTerm.trim() || getSearchText(deal).includes(slugify(searchTerm));
            const discountMatches = getDiscountNumber(deal) >= Number(minDiscount || 0);
            const price = Number(deal.price || 0);
            const minPriceMatches = !Number(minPrice) || price >= Number(minPrice);
            const maxPriceMatches = !Number(maxPrice) || price <= Number(maxPrice);
            const heatMatches = !onlyHot || Number(deal.heat || 0) >= 180;

            return categoryMatches && storeMatches && searchMatches && discountMatches && minPriceMatches && maxPriceMatches && heatMatches;
        });

        // La agrupacion de variantes de talla ya la hace el servidor
        // (server/lib/productVariants.js, groupDealVariants sobre
        // /api/deals) -- este componente solo ordena/filtra lo que ya llega
        // agrupado. Hasta el 2026-09-04 habia ademas una segunda agrupacion
        // aqui en el cliente (collapseSizeVariants); las dos coexistiendo
        // hacian mas dano que bien: cada una reconocia un subconjunto
        // distinto de formatos de talla (medias tallas "36.5", fracciones
        // "37 1/3", tallas infantiles 24-29...), asi que una tienda con
        // variantes de varios formatos a la vez terminaba con la tarjeta
        // agrupada del servidor MAS tarjetas sueltas por cada formato que el
        // servidor no reconocia todavia -- justo el bug real reportado en
        // /tiendas/4-elementos. Retirada al arreglar esos formatos en el
        // servidor (unico sitio que agrupa, en vez de dos a medias).
        return [...filteredDeals].sort((dealA, dealB) => sortDeals(dealA, dealB, sortBy));
    }, [activeCategorySlug, initialStore, minDiscount, minPrice, maxPrice, onlyHot, searchTerm, sortBy, sourceDeals, useDevelopmentFallback]);

    const inlineExclusionKey = useMemo(() => {
        if (!inlineFeatured || !hasApiResponse || visibleDeals.length === 0) return '';
        return [...new Set([
            ...inlineFeaturedExcludeIds,
            ...visibleDeals.slice(0, pageSize).map((deal) => deal.id)
        ].map((id) => Number(id)).filter((id) => Number.isSafeInteger(id) && id > 0))].join(',');
    }, [inlineFeatured, inlineFeaturedExcludeIds, hasApiResponse, visibleDeals, pageSize]);

    useEffect(() => {
        if (!inlineFeatured || !inlineExclusionKey) {
            setInlineFeaturedDeals([]);
            return undefined;
        }
        const controller = new AbortController();
        // El listado principal se asienta en un par de renders mientras carga
        // (paginacion, respuesta progresiva); sin este margen, cada cambio
        // intermedio de inlineExclusionKey disparaba su propia peticion a
        // /api/deals/featured, duplicando la espera del hueco patrocinado.
        const timer = setTimeout(() => {
            const params = new URLSearchParams({ placement: 'scroll', exclude: inlineExclusionKey });
            fetchJsonWithBusyRetry(`/api/deals/featured?${params.toString()}`, { signal: controller.signal })
                .then((data) => setInlineFeaturedDeals(Array.isArray(data.deals) ? data.deals : []))
                .catch((error) => {
                    if (error.name !== 'AbortError') setInlineFeaturedDeals([]);
                });
        }, 250);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [inlineFeatured, inlineExclusionKey]);

    const summary = useMemo(() => {
        const hotCount = visibleDeals.filter((deal) => Number(deal.heat || 0) >= 180).length;
        const bestDiscount = visibleDeals.reduce((max, deal) => Math.max(max, getDiscountNumber(deal)), 0);
        const storesCount = new Set(visibleDeals.map((deal) => slugify(deal.store))).size;

        return { hotCount, bestDiscount, storesCount };
    }, [visibleDeals]);

    // BUG REAL reportado por el usuario (2026-09-13): al llegar cerca del
    // final, "se pone a cargar a lo loco muchos articulos y no para". Causa
    // raiz: el efecto de abajo recreaba el IntersectionObserver entero cada
    // vez que `isLoading`/`visibleDeals.length` cambiaban (estaban en las
    // dependencias) -- justo el patron que dispara esto: cada vez que una
    // pagina terminaba de cargar, se destruia el observer viejo y se creaba
    // uno nuevo, y un IntersectionObserver recien creado evalua el estado
    // ACTUAL del elemento al momento de empezar a observarlo (no espera a
    // un cambio futuro) -- si el centinela seguia dentro del margen de
    // deteccion (rootMargin, generoso a proposito para precargar antes de
    // que el usuario vea un hueco), el observer nuevo disparaba
    // "isIntersecting" de inmediato, pidiendo la SIGUIENTE pagina otra vez,
    // en cascada, sin que el usuario siguiera haciendo scroll -- cada
    // pagina cargada volvia a recrear el observer y a repetirse, hasta
    // agotar `hasMore` o hasta que el contenido acumulado por fin empujara
    // el centinela fuera del margen de 500px.
    //
    // Fix: el observer YA NO se recrea por `isLoading` (el disparador real
    // del bug) ni por el numero crudo de `visibleDeals.length` -- pero SI
    // sigue dependiendo de `hasMore` (a diferencia de isLoading, hasMore
    // cambia raras veces: pasa de true a false UNA vez cuando de verdad se
    // agota el catalogo, no en cada pagina cargada) y de `nodeCanExist`
    // (el nodo con la ref solo existe una vez hay datos, ver mas abajo) --
    // asi se evita crear un observer inutil cuando ya no hay mas que
    // cargar, sin reintroducir la cascada original. El callback lee el
    // estado ACTUAL (`isLoading` incluido) desde una ref
    // (loadMoreStateRef, sincronizada en cada render mas abajo) en vez de
    // una clausura vieja, como defensa adicional ante una carga en curso.
    loadMoreStateRef.current = { hasMore, isLoading, hasItems: visibleDeals.length > 0 };

    // El nodo con la ref (mas abajo en el JSX) solo se renderiza cuando
    // `hasApiResponse && visibleDeals.length > 0` -- en el primer render
    // (antes de que llegue cualquier dato) el nodo aun no existe. Por eso
    // el efecto SI necesita re-intentar una vez que el nodo pasa de no
    // existir a existir (`nodeCanExist`, un booleano que solo cambia UNA
    // vez por busqueda/filtro, no en cada pagina) -- a diferencia de
    // `visibleDeals.length` en crudo, este booleano no oscila en cada
    // pagina cargada, asi que no reintroduce la cascada del bug original.
    const nodeCanExist = hasApiResponse && visibleDeals.length > 0;

    // Reporte real de usuario (2026-09-22): "nos quedamos congelados al
    // final de la pagina y no apreciamos que se cargue nada" -- con 500px de
    // margen, en una rejilla de varias columnas eso apenas cubre una fila de
    // tarjetas, y en esta instancia una peticion en frio puede tardar varios
    // segundos (ver AGENTS.md, "lectura fria"/staleWhileRevalidate) -- el
    // usuario a menudo alcanzaba el centinela antes de que la pagina
    // siguiente hubiera terminado de llegar. Subido a 1600px (varias
    // pantallas de antelacion en movil y escritorio) para que la carga
    // termine de verdad ANTES de que el hueco sea visible, no solo se
    // dispare antes -- sin esto el fix de abajo (ocultar el boton manual)
    // habria dejado un hueco vacio en vez de una carga imperceptible.
    useEffect(() => {
        const node = loadMoreRef.current;
        if (!autoLoadMore || !node || !hasMore) {
            return undefined;
        }

        const observer = new IntersectionObserver((entries) => {
            const { hasMore: canLoadMore, isLoading: alreadyLoading, hasItems } = loadMoreStateRef.current;
            if (entries[0]?.isIntersecting && canLoadMore && !alreadyLoading && hasItems) {
                setPage((currentPage) => currentPage + 1);
            }
        }, { rootMargin: '1600px 0px' });

        observer.observe(node);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoLoadMore, nodeCanExist, hasMore]);

    // Fallback de scroll para navegadores/dispositivos donde el
    // IntersectionObserver del centinela no dispare (reportado como
    // intermitente: el usuario llega al final, ve "Desplazate para ver mas
    // ofertas...", pero no se carga nada). Este listener es redundante con el
    // observer pero no compite: ambos leen el mismo estado actual desde
    // loadMoreStateRef y solo incrementan page cuando no hay carga en curso.
    useEffect(() => {
        if (!autoLoadMore || !hasMore) {
            return undefined;
        }
        let ticked = false;
        const checkNearBottom = () => {
            if (ticked) return;
            ticked = true;
            requestAnimationFrame(() => {
                ticked = false;
                const scrollHeight = document.documentElement.scrollHeight;
                const scrollTop = window.scrollY || document.documentElement.scrollTop;
                const clientHeight = window.innerHeight;
                if (scrollHeight <= clientHeight) return;
                if (scrollTop + clientHeight >= scrollHeight - 1600) {
                    const { hasMore: canLoadMore, isLoading: alreadyLoading, hasItems } = loadMoreStateRef.current;
                    if (canLoadMore && !alreadyLoading && hasItems) {
                        setPage((currentPage) => currentPage + 1);
                    }
                }
            });
        };
        window.addEventListener('scroll', checkNearBottom, { passive: true });
        window.addEventListener('resize', checkNearBottom, { passive: true });
        checkNearBottom();
        return () => {
            window.removeEventListener('scroll', checkNearBottom);
            window.removeEventListener('resize', checkNearBottom);
        };
    }, [autoLoadMore, hasMore]);

    const activeCategoryName = activeCategorySlug ? findCategory(activeCategorySlug)?.name || formatCategory(activeCategorySlug) : 'Todas';
    const alertSearchKeyword = searchTerm.trim();
    const categoryStatLabel = activeCategorySlug ? 'categoria' : 'categorias';
    const activeAdvancedFilterCount = [
        Boolean(showFilters && activeCategorySlug),
        minDiscount !== '0',
        Boolean(minPrice || maxPrice),
        onlyHot,
        sortBy !== initialSort,
        Boolean(selectedProductType)
    ].filter(Boolean).length + Object.values(selectedAttributes).filter(Boolean).length;
    const hasActiveTools = Boolean(searchTerm.trim()) || activeAdvancedFilterCount > 0;
    const isTravelContext = activeCategorySlug === 'viajes' || selectedProductType === 'travel';
    const activeTravelKind = selectedAttributes.travel_kind || '';

    const resetFilters = () => {
        setActiveCategorySlug('');
        setSearchTerm('');
        setSortBy(initialSort);
        setMinDiscount('0');
        setMinPrice('');
        setMaxPrice('');
        setOnlyHot(false);
        setSelectedProductType('');
        setSelectedAttributes({});
        setComparedDeals([]);
        if (location.pathname.startsWith('/chollos/')) {
            navigate('/buscar');
        }
    };

    const handleCategoryChange = (event) => {
        const nextCategorySlug = event.target.value;
        setActiveCategorySlug(nextCategorySlug);

        if (nextCategorySlug) {
            navigate(`/chollos/${nextCategorySlug}`, { state: { keepFiltersOpen: true, preserveScroll: true } });
            return;
        }

        if (location.pathname.startsWith('/chollos/')) {
            navigate('/buscar');
        }
    };

    const handleSaveDeal = (deal) => {
        addToCart(deal);

        if (!user) {
            toast.success(`${deal.name} guardado para revisar despues. Inicia sesion para que tambien te avisemos si baja de precio.`);
            return;
        }

        fetch('/api/alerts/watch-deal', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dealId: deal.id })
        })
            .then((response) => (response.ok ? response.json() : Promise.reject(new Error('watch-failed'))))
            .then(() => {
                toast.success(`${deal.name} guardado. Te avisamos por email si baja de precio.`);
                offerInstantPushIfNotSubscribed();
            })
            .catch(() => toast.success(`${deal.name} guardado para revisar despues.`));
    };

    // Solo se ofrece una vez por sesion de guardado (no en cada "Guardar")
    // para no ser pesados: si el navegador ya esta suscrito, no hace nada.
    const offerInstantPushIfNotSubscribed = () => {
        if (!isPushSupported()) return;
        getExistingSubscription().then((subscription) => {
            if (subscription) return;
            toast.info(
                <span>
                    ¿Avisos instantaneos en este dispositivo, ademas del email?{' '}
                    <button
                        type="button"
                        className={styles.pushToastButton}
                        onClick={() => subscribeToPush().then(() => toast.success('Avisos instantaneos activados.')).catch(() => {})}
                    >
                        Activar
                    </button>
                </span>,
                { autoClose: 8000 }
            );
        });
    };

    const handleToggleComparison = (deal) => {
        if (comparedDeals.some((item) => String(item.id) === String(deal.id))) {
            setComparedDeals((current) => current.filter((item) => String(item.id) !== String(deal.id)));
            return;
        }
        if (!deal.comparisonProfile?.type) return;
        if (comparedDeals.length >= 3) {
            toast.info('Puedes comparar hasta tres productos a la vez.');
            return;
        }
        if (comparedDeals.length > 0 && comparedDeals[0].comparisonProfile?.type !== deal.comparisonProfile.type) {
            toast.info('Compara móviles con móviles o televisores con televisores.');
            return;
        }

        setComparedDeals((current) => [...current, deal]);
    };

    const openComparison = () => {
        navigate(`/comparar?ids=${comparedDeals.map((deal) => deal.id).join(',')}`, {
            state: { deals: comparedDeals }
        });
    };

    const handleProductTypeChange = (event) => {
        setSelectedProductType(event.target.value);
        setSelectedAttributes({});
    };

    const handleAttributeChange = (key, value) => {
        setSelectedAttributes((current) => ({
            ...current,
            [key]: value
        }));
    };

    const handleTravelQuickFilter = (travelKind) => {
        setActiveCategorySlug('viajes');
        setSelectedProductType('travel');
        setSelectedAttributes((current) => ({
            ...current,
            travel_kind: travelKind
        }));

        if (location.pathname.startsWith('/chollos/') && !location.pathname.startsWith('/chollos/viajes')) {
            navigate('/chollos/viajes');
        }
    };

    return (
        <section className={styles.dealsSection} id="chollos" aria-labelledby="deals-heading">
            <div className={styles.sectionHeader}>
                <div>
                    <p className={styles.eyebrow}>{eyebrow}</p>
                    <h2 id="deals-heading">{title}</h2>
                </div>
                <p>{description}</p>
            </div>

            {(showFilters || showControls) && (
                <div className={styles.filterShell}>
                    <div className={styles.filterHeader}>
                        <p className={styles.filterKicker}>Filtros</p>
                        {showControls && (
                            <button
                                type="button"
                                className={styles.filterToggle}
                                onClick={() => setFiltersOpen((open) => !open)}
                                aria-expanded={filtersOpen}
                                aria-controls={advancedFiltersId}
                                aria-label={filtersOpen
                                    ? 'Ocultar filtros'
                                    : activeAdvancedFilterCount > 0
                                        ? `Más filtros, ${activeAdvancedFilterCount} activos`
                                        : 'Más filtros'}
                            >
                                {filtersOpen ? 'Ocultar filtros' : 'Más filtros'}
                                {activeAdvancedFilterCount > 0 && !filtersOpen && (
                                    <span className={styles.filterToggleBadge} aria-hidden="true">
                                        {activeAdvancedFilterCount}
                                    </span>
                                )}
                            </button>
                        )}
                    </div>

                    {showControls && (
                        <div className={styles.dealToolbarAlways} aria-label="Buscar ofertas">
                            <div className={styles.searchControl}>
                                <span>Buscar oferta</span>
                                <SearchAutocomplete
                                    value={searchTerm}
                                    placeholder="Xiaomi, PS5, freidora, vuelos..."
                                    ariaLabel="Buscar oferta"
                                    onChange={setSearchTerm}
                                    onSubmit={(query) => navigate(`/buscar?q=${encodeURIComponent(query)}`)}
                                />
                            </div>
                            {alertSearchKeyword.length >= 2 && (
                                <Link
                                    className={styles.searchAlertCta}
                                    to={buildAlertCtaUrl({
                                        keyword: alertSearchKeyword,
                                        category: activeCategorySlug,
                                        maxPrice,
                                        source: 'search'
                                    })}
                                >
                                    🔔 Avísame si aparece
                                </Link>
                            )}
                        </div>
                    )}

                    {showControls && isTravelContext && (
                        <div className={styles.travelQuickFilters} aria-label="Filtros rapidos de viajes">
                            <span>Tipo de viaje</span>
                            <div>
                                {travelQuickFilters.map((filter) => (
                                    <button
                                        key={filter.value || 'all-travel'}
                                        type="button"
                                        className={activeTravelKind === filter.value ? styles.travelQuickFilterActive : styles.travelQuickFilter}
                                        onClick={() => handleTravelQuickFilter(filter.value)}
                                    >
                                        {filter.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {showControls && filtersOpen && (
                        <div id={advancedFiltersId}>
                            <div className={styles.dealToolbar} aria-label="Herramientas para encontrar mejores gangas">
                                {showFilters && (
                                    <label className={styles.filterField}>
                                        <span>Categoria</span>
                                        <select value={activeCategorySlug} onChange={handleCategoryChange}>
                                            {categoryOptions.map((category) => (
                                                <option key={category.slug || 'all'} value={category.slug}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                <label className={styles.filterField}>
                                    <span>Ordenar por</span>
                                    <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                                        {(searchTerm.trim() || sortBy === 'relevance') && <option value="relevance">Mas relevantes</option>}
                                        <option value="recent">Recientes</option>
                                        <option value="heat">Mas populares</option>
                                        <option value="discount">Mayor descuento</option>
                                        <option value="price">Precio mas bajo</option>
                                    </select>
                                </label>
                                <label className={styles.filterField}>
                                    <span>Descuento minimo</span>
                                    <select value={minDiscount} onChange={(event) => setMinDiscount(event.target.value)}>
                                        <option value="0">Cualquiera</option>
                                        <option value="20">20% o mas</option>
                                        <option value="30">30% o mas</option>
                                        <option value="50">50% o mas</option>
                                    </select>
                                </label>
                                <div
                                    className={styles.priceRangeControl}
                                    role="group"
                                    aria-label="Rango de precio"
                                >
                                    <span className={styles.priceRangeLabel}>Precio</span>
                                    <div className={styles.priceRangeInputs}>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            inputMode="decimal"
                                            value={minPrice}
                                            placeholder="Min"
                                            aria-label="Precio minimo"
                                            onChange={(event) => setMinPrice(event.target.value)}
                                        />
                                        <span className={styles.priceRangeDash} aria-hidden="true">-</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            inputMode="decimal"
                                            value={maxPrice}
                                            placeholder="Max"
                                            aria-label="Precio maximo"
                                            onChange={(event) => setMaxPrice(event.target.value)}
                                        />
                                    </div>
                                </div>
                                <label className={styles.hotToggle}>
                                    <input
                                        type="checkbox"
                                        checked={onlyHot}
                                        onChange={(event) => setOnlyHot(event.target.checked)}
                                    />
                                    <span className={styles.switchTrack} aria-hidden="true">
                                        <span className={styles.switchThumb} />
                                    </span>
                                    <span>Solo destacadas</span>
                                </label>
                            </div>

                            {facetData && (
                                <div className={styles.dynamicFilters} aria-label="Filtros especificos del producto">
                                    {Array.isArray(facetData.productTypes) && (facetData.productTypes.length > 1 || selectedProductType) && (
                                        <label>
                                            <span>Tipo de producto</span>
                                            <select value={selectedProductType} onChange={handleProductTypeChange}>
                                                <option value="">Todos los tipos</option>
                                                {facetData.productTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    )}
                                    {(facetData.facets || []).map((facet) => (
                                        <label key={facet.key}>
                                            <span>{facet.label}</span>
                                            <select
                                                value={selectedAttributes[facet.key] || ''}
                                                onChange={(event) => handleAttributeChange(facet.key, event.target.value)}
                                            >
                                                <option value="">Cualquiera</option>
                                                {facet.options.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {formatFacetValue(facet, option.value)}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className={styles.dealStats} aria-label="Resumen de gangas visibles">
                        {isLoading && page === 0 ? (
                            <p className={styles.searchingIndicator} aria-live="polite">
                                <span className={styles.searchingSpinner} aria-hidden="true" />
                                <strong>{visibleDeals.length === 0 ? 'Cargando ofertas...' : 'Buscando...'}</strong>{' '}
                                {debouncedSearchTerm
                                    ? `Actualizando resultados para "${debouncedSearchTerm}".`
                                    : 'Preparando resultados actuales.'}
                            </p>
                        ) : apiFailed && !useDevelopmentFallback ? (
                            <p><strong>Datos no disponibles.</strong> Puedes reintentar sin salir de la página.</p>
                        ) : (
                            <p>
                                <strong>{activeCategoryName}</strong> {categoryStatLabel} ·{' '}
                                <strong>{summary.hotCount}</strong> destacadas ·{' '}
                                {summary.bestDiscount > 0 && <><strong>{summary.bestDiscount}%</strong> mejor descuento · </>}
                                <strong>{summary.storesCount}</strong> tiendas
                            </p>
                        )}
                        {correctedSearch && !(isLoading && page === 0) && (
                            <p className={styles.searchCorrection} role="status">
                                Mostrando resultados para <strong>«{correctedSearch}»</strong>
                            </p>
                        )}
                        {hasActiveTools && (
                            <button type="button" className={styles.resetButton} onClick={resetFilters}>
                                Limpiar filtros
                            </button>
                        )}
                    </div>
                </div>
            )}

            {comparedDeals.length > 0 && (
                <div className={styles.comparisonBar} role="region" aria-label="Comparador de productos" aria-live="polite">
                    <div className={styles.comparisonBarSummary}>
                        <strong>
                            <span className={styles.comparisonBarCount} aria-hidden="true">{comparedDeals.length}/3</span>
                            {comparedDeals.length} de 3 seleccionado{comparedDeals.length === 1 ? '' : 's'}
                        </strong>
                        <span>
                            {comparedDeals.length === 1
                                ? 'Elige al menos otro producto del mismo tipo.'
                                : comparedDeals.length === 2
                                    ? 'Ya puedes comparar o añadir uno más.'
                                    : 'Selección completa: ya puedes comparar.'}
                        </span>
                    </div>
                    <div className={styles.comparisonBarActions}>
                        <button type="button" onClick={() => setComparedDeals([])}>Limpiar</button>
                        <button type="button" onClick={openComparison} disabled={comparedDeals.length < 2}>
                            {comparedDeals.length < 2 ? 'Selecciona otro' : `Comparar ahora (${comparedDeals.length})`}
                        </button>
                    </div>
                </div>
            )}

            <div
                className={isLoading && page === 0 && visibleDeals.length > 0 ? `${styles.productList} ${styles.productListRefreshing}` : styles.productList}
                aria-busy={isLoading && page === 0 && visibleDeals.length > 0}
            >
                {visibleDeals.map((deal, index) => {
                    const price = Number(deal.price || 0);
                    const oldPrice = Number(deal.oldPrice || 0);
                    const discountNumber = getDiscountNumber(deal);
                    const hasRealDiscount = discountNumber > 0 && oldPrice > price;
                    const saving = oldPrice > price ? oldPrice - price : 0;
                    const confidence = getConfidenceLabel(deal, saving);
                    const isCompared = comparedDeals.some((item) => String(item.id) === String(deal.id));

                    const inlineDeal = (index + 1) % 12 === 0
                        ? inlineFeaturedDeals[Math.floor((index + 1) / 12) - 1]
                        : null;
                    // Rotacion cada 15 productos, en TODO el scroll infinito.
                    // 3 familias en vez de las 2 originales (telegram/
                    // newsletter): se añadio 'amazon' a peticion del usuario,
                    // rotando ademas ENTRE los 9 enlaces reales de
                    // AMAZON_PROMOS cada vez que le toca el turno a esa
                    // familia (contador independiente, amazonCycleCount mas
                    // abajo) -- asi ninguno de los 9 acapara el hueco, todos
                    // se ven con el tiempo segun el usuario sigue haciendo
                    // scroll. Se salta si ese mismo indice ya lo ocupa un
                    // anuncio destacado (inlineDeal), para no competir dos
                    // tarjetas especiales por el mismo hueco.
                    const promoCycleIndex = Math.floor((index + 1) / 15) - 1;
                    const promoFamily = !inlineDeal && (index + 1) % 15 === 0
                        ? ['telegram', 'newsletter', 'amazon'][((promoCycleIndex % 3) + 3) % 3]
                        : null;
                    const promoVariant = promoFamily;
                    const amazonCycleCount = Math.floor(promoCycleIndex / 3);
                    const amazonPromo = promoVariant === 'amazon'
                        ? AMAZON_PROMOS[((amazonCycleCount % AMAZON_PROMOS.length) + AMAZON_PROMOS.length) % AMAZON_PROMOS.length]
                        : null;

                    return (
                    <React.Fragment key={deal.id}>
                    {promoVariant && <InlinePromoCard variant={promoVariant} amazonPromo={amazonPromo} />}
                    <article className={styles.productItem}>
                        <Link
                            to={`/ofertas/${deal.id}`}
                            state={{ deal }}
                            className={styles.imageWrap}
                            aria-label={`Ver detalle de ${deal.name}`}
                        >
                            <SensitiveThumbnail
                                isSensitive={slugify(deal.category) === 'erotico-adultos'}
                                src={withShopifyWidth(deal.image, 500)}
                                alt={deal.name}
                                className={styles.productImage}
                                loading={index === 0 ? 'eager' : 'lazy'}
                                decoding="async"
                                fetchpriority={index === 0 && priorityFirstImage ? 'high' : 'auto'}
                            />
                            {hasRealDiscount && (
                                <span
                                    className={discountNumber >= 70 ? `${styles.discountBadge} ${styles.discountBadgeHot}` : styles.discountBadge}
                                    aria-hidden="true"
                                >
                                    -{discountNumber}%
                                </span>
                            )}
                            {Number(deal.heat || 0) >= 180 && <span className={styles.heatBadge} aria-hidden="true">🔥 Top</span>}
                        </Link>
                        <div className={styles.productMeta}>
                            <SellerBadge listingOrigin={deal.listingOrigin} />
                            <span>{deal.store}</span>
                            <span>{formatCategory(deal.category)}</span>
                        </div>
                        {deal.comparisonProfile && (
                            <button
                                type="button"
                                className={isCompared ? styles.compareChoiceActive : styles.compareChoice}
                                aria-pressed={isCompared}
                                onClick={() => handleToggleComparison(deal)}
                            >
                                <span aria-hidden="true">{isCompared ? '✓' : '+'}</span>
                                {isCompared ? 'En comparación' : 'Comparar'}
                            </button>
                        )}
                        <button
                            type="button"
                            className={favoriteIds?.has(deal.id) ? styles.favoriteToggleActive : styles.favoriteToggle}
                            aria-pressed={favoriteIds?.has(deal.id) || false}
                            aria-label={favoriteIds?.has(deal.id) ? `Quitar ${deal.name} de favoritos` : `Anadir ${deal.name} a favoritos`}
                            onClick={() => toggleFavorite(deal)}
                        >
                            <span aria-hidden="true">{favoriteIds?.has(deal.id) ? '★' : '☆'}</span>
                            {favoriteIds?.has(deal.id) ? 'En favoritos' : 'Favorito'}
                        </button>
                        <h3 className={styles.productName}>
                            <Link to={`/ofertas/${deal.id}`} state={{ deal }}>{deal.name}</Link>
                        </h3>
                        <p className={styles.productDescription}>{deal.description}</p>
                        {Array.isArray(deal.variants) && deal.variants.length > 1 && (
                            <div className={styles.offersRow} aria-label="Tallas disponibles">
                                <span className={styles.offersLabel}>Tallas:</span>
                                {deal.variants.map((variant) => (
                                    <Link
                                        key={variant.id}
                                        to={`/ofertas/${variant.id}`}
                                        className={styles.offerChip}
                                    >
                                        {variant.size}
                                    </Link>
                                ))}
                            </div>
                        )}
                        <div className={styles.signalRow} aria-label="Senales de calidad de la ganga">
                            <span className={styles.signalConfidence}>{confidence}</span>
                            {saving > 0 && <span className={styles.signalSaving}>Ahorro {saving.toFixed(2)}€</span>}
                            {Number(deal.heat || 0) >= 180 && <span className={styles.signalHot}>🔥 Descuento destacado</span>}
                            {deal.attributes?.freeShipping && <span className={styles.signalShipping}>🚚 Envío gratis</span>}
                            {deal.attributes?.condition && deal.attributes.condition !== 'Nuevo' && (
                                <span className={styles.signalCondition}>{deal.attributes.condition}</span>
                            )}
                        </div>
                        <div className={styles.priceRow}>
                            <p className={styles.productPrice}>{price.toFixed(2)}€</p>
                            {oldPrice > price && <p className={styles.oldPrice}>{oldPrice.toFixed(2)}€</p>}
                        </div>
                        {Array.isArray(deal.offers) && deal.offers.length > 0 && (
                            <div className={styles.offersRow} aria-label="Tambien disponible en otras tiendas">
                                <span className={styles.offersLabel}>Tambien en:</span>
                                {deal.offers.slice(0, 4).map((offer) => (
                                    <a
                                        key={offer.id}
                                        href={offer.url}
                                        target="_blank"
                                        rel="sponsored nofollow noopener noreferrer"
                                        className={styles.offerChip}
                                        onClick={() => trackClick(offer.id, offer.clickRef)}
                                    >
                                        <StoreIcon name={offer.store} logo={offer.storeLogo} />
                                        {Number(offer.price || 0).toFixed(2)}€
                                    </a>
                                ))}
                            </div>
                        )}
                        <div className={styles.cardFooter}>
                            <span>{deal.highlight || (deal.listingOrigin === 'seller' ? 'Anuncio' : 'Oferta seleccionada')}</span>
                            <span>{deal.expires || formatFreshness(deal.updatedAt)}</span>
                        </div>
                        <div className={styles.actions}>
                            <a
                                href={deal.url}
                                target="_blank"
                                rel="sponsored nofollow noopener noreferrer"
                                className={styles.addToCartButton}
                                onClick={() => trackClick(deal.id, deal.clickRef)}
                            >
                                Ver oferta
                            </a>
                            <Link
                                to={`/ofertas/${deal.id}`}
                                state={{ deal }}
                                className={styles.detailButton}
                            >
                                Ver detalles
                            </Link>
                            <Link
                                to={buildAlertCtaUrl({ keyword: deal.name, category: deal.category, source: 'deal' })}
                                className={styles.alertCardButton}
                            >
                                🔔 Avísame si baja
                            </Link>
                            <button
                                className={styles.saveButton}
                                onClick={() => handleSaveDeal(deal)}
                                aria-label={`Guardar ${deal.name}`}
                            >
                                Guardar
                            </button>
                        </div>
                    </article>
                    {inlineDeal && <InlineFeaturedCard deal={inlineDeal} />}
                    </React.Fragment>
                );
                })}
                {isLoading && visibleDeals.length === 0 && [0, 1, 2, 3, 4, 5].map((key) => (
                    <article key={`loading-${key}`} className={styles.productSkeleton} aria-hidden="true">
                        <span className={styles.skeletonImage} />
                        <span className={styles.skeletonLineShort} />
                        <span className={styles.skeletonLine} />
                        <span className={styles.skeletonLine} />
                        <span className={styles.skeletonPrice} />
                        <span className={styles.skeletonButton} />
                    </article>
                ))}
            </div>

            {hasApiResponse && visibleDeals.length > 0 && (
                <div className={styles.loadMoreArea} ref={loadMoreRef}>
                    {/* BUG REAL reportado por el usuario (2026-09-24): con
                        scroll rapido (flick en movil, o un salto grande de
                        scroll), el centinela puede caer fuera del rootMargin
                        justo despues de renderizar una pagina nueva -- antes
                        de que `isLoading` volviera a activarse, esta zona se
                        quedaba en BLANCO (ninguna de las ramas de abajo
                        aplicaba) durante el tiempo que tardaba el usuario en
                        acercarse otra vez lo suficiente para que el
                        IntersectionObserver disparara la siguiente pagina.
                        Confirmado en produccion real: el scroll SI seguia
                        funcionando con un poco mas de scroll, pero un hueco en
                        blanco sin ningun aviso da la sensacion exacta de "se
                        quedo colgado, no hay forma de cargar mas" que
                        reporto el usuario. Con scroll automatico activo y
                        catalogo con mas paginas, esta zona ya NUNCA debe
                        quedar vacia -- si no esta cargando de verdad todavia,
                        sigue siendo honesto avisar que hay mas viniendo.

                        BUG REAL reportado de nuevo por el usuario
                        (2026-10-03, "entro a la principal y me muestra
                        productos, pero me sale cargando"): la condicion era
                        `isLoading || (autoLoadMore && hasMore)`, o sea que el
                        texto "Cargando mas ofertas..." salia ALSO cuando NO
                        habia ninguna peticion en curso -- con la primera pagina
                        ya cargada y hasMore=true, en reposo, esperando a que el
                        observer llegara al centinela. El usuario veia "cargando"
                        sin que hubiera nada cargandose, y lo leia como pagina
                        colgada. Ahora "Cargando mas ofertas..." es LITERAL: solo
                        con isLoading. En reposo hay mas, el aviso honesto es
                        que hay que desplazar, no que estamos cargando. */}
                    {isLoading && <span>Cargando mas ofertas...</span>}
                    {!isLoading && hasMore && autoLoadMore && <span>Desplazate para ver mas ofertas...</span>}
                    {!isLoading && hasMore && browseMoreHref && (
                        <Link to={browseMoreHref}>{browseMoreLabel}</Link>
                    )}
                    {/* Reporte real de usuario (2026-09-22): con el scroll
                        automatico ya activo (autoLoadMore, el valor por
                        defecto en toda la web -- home, /buscar, categoria,
                        tienda) este boton seguia mostrandose siempre que
                        hasMore fuera true, dando la sensacion de que hacia
                        falta pulsarlo mientras la siguiente pagina en
                        realidad ya se estaba cargando sola por detras. Ahora
                        solo aparece cuando NO hay scroll automatico (fallback
                        real para esos casos, ver el test de
                        "pagina curada finita" con autoLoadMore={false}). */}
                    {!isLoading && hasMore && !browseMoreHref && !autoLoadMore && (
                        <button type="button" onClick={() => setPage((currentPage) => currentPage + 1)}>
                            Cargar mas ofertas
                        </button>
                    )}
                    {!isLoading && !hasMore && <span>Has visto todas las ofertas disponibles con estos filtros.</span>}
                </div>
            )}

            {hasApiResponse && !isLoading && visibleDeals.length === 0 && (
                <div className={styles.emptyState}>
                    <strong>No la tenemos localizada todavia</strong>
                    <span>
                        Dinos que producto, tienda o viaje buscas y lo intentamos encontrar por otro lado.
                    </span>
                    <div className={styles.emptyActions}>
                        <Link to={`/contact?oferta=${encodeURIComponent(searchTerm || activeCategoryName)}`}>
                            Pedir que la busquemos
                        </Link>
                        {alertSearchKeyword.length >= 2 && (
                            <Link
                                className={styles.alertEmptyAction}
                                to={buildAlertCtaUrl({
                                    keyword: alertSearchKeyword,
                                    category: activeCategorySlug,
                                    maxPrice,
                                    source: 'search'
                                })}
                            >
                                🔔 Avísame cuando aparezca
                            </Link>
                        )}
                        {hasActiveTools && (
                            <button type="button" onClick={resetFilters}>Ver todas las ofertas</button>
                        )}
                    </div>
                </div>
            )}

            {apiFailed && !useDevelopmentFallback && (
                <div className={styles.emptyState} role="alert">
                    <strong>No hemos podido cargar las ofertas</strong>
                    <span>La página sigue disponible. Reintenta la carga para recuperar los productos actuales.</span>
                    <div className={styles.emptyActions}>
                        <button
                            type="button"
                            onClick={() => {
                                setApiFailed(false);
                                setIsLoading(true);
                                setReloadToken((value) => value + 1);
                            }}
                        >
                            Reintentar
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

// Tarjeta promocional con el mismo tamano/forma que una tarjeta de producto
// real, intercalada en el listado -- mismo patron ya usado por
// Chollometro para su aviso de Telegram (se mezcla visualmente con los
// chollos reales en vez de un banner aparte). Solo aparece una vez por
// pagina cargada (ver promoVariant en el bucle de arriba), no en cada tanda
// del scroll infinito.
function InlinePromoCard({ variant, amazonPromo }) {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle');

    const handleNewsletterSubmit = async (event) => {
        event.preventDefault();
        if (status === 'sending') {
            return;
        }
        setStatus('sending');
        try {
            const response = await fetch('/api/newsletter/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await response.json();
            if (!response.ok || !data.ok) {
                throw new Error(data.error || 'No se pudo suscribir');
            }
            toast.success('Listo, cada viernes te llegan las 10 mejores gangas.');
            setStatus('done');
        } catch (error) {
            toast.error('Revisa el correo e intentalo de nuevo.');
            setStatus('idle');
        }
    };

    if (variant === 'amazon' && amazonPromo) {
        // Fire-and-forget: la analitica nunca debe retrasar ni bloquear el
        // clic real del usuario hacia Amazon (mismo criterio que
        // trackOutboundClick en el resto del fichero).
        const handleAmazonClick = () => {
            fetch(`/api/promo/${amazonPromo.id}/click`, { method: 'POST' }).catch(() => {});
        };
        return (
            <article className={`${styles.productItem} ${styles.inlineFeaturedItem}`} aria-label={amazonPromo.title}>
                <div className={styles.inlineFeaturedHeading}>
                    <span className={styles.inlineHookBadge}>{amazonPromo.badge}</span>
                </div>
                <div className={styles.imageWrap} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c43e2b' }}>
                    <AmazonPromoIcon id={amazonPromo.id} size={72} />
                </div>
                <h3 className={styles.productName}>{amazonPromo.title}</h3>
                <p className={styles.productDescription}>{amazonPromo.description}</p>
                <div className={styles.actions}>
                    <a
                        href={amazonPromo.href}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={handleAmazonClick}
                        className={styles.addToCartButton}
                    >
                        {amazonPromo.cta}
                    </a>
                </div>
            </article>
        );
    }

    const isTelegram = variant === 'telegram';

    return (
        <article className={`${styles.productItem} ${styles.inlineFeaturedItem}`} aria-label={isTelegram ? 'Unete al Telegram de Esganga' : 'Suscribete a la newsletter de Esganga'}>
            <div className={styles.inlineFeaturedHeading}>
                <span className={styles.inlineHookBadge}>{isTelegram ? 'Telegram' : 'Newsletter'}</span>
                <span>Gratis</span>
            </div>
            <div className={styles.imageWrap} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isTelegram
                    ? <img src={telegramIcon} alt="" className={styles.productImage} style={{ padding: 32 }} loading="lazy" />
                    : (
                        <svg viewBox="0 0 24 24" width="72" height="72" fill="none" stroke="#c43e2b" strokeWidth="1.6" aria-hidden="true">
                            <rect x="3" y="5" width="18" height="14" rx="2" />
                            <path d="m4 7 8 6 8-6" />
                        </svg>
                    )}
            </div>
            <h3 className={styles.productName}>
                {isTelegram
                    ? '¿Quieres avisos GRATIS de las mejores gangas vía Telegram?'
                    : 'Las 10 mejores gangas de la semana, cada viernes en tu correo'}
            </h3>
            <p className={styles.productDescription}>
                {isTelegram
                    ? 'Notificaciones al instante de las mejores ofertas, directo a tu movil. Sal cuando quieras.'
                    : 'Un unico correo semanal, sin spam. Solo ofertas que siguen activas.'}
            </p>
            <div className={styles.actions}>
                {isTelegram ? (
                    <a
                        href={TELEGRAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.addToCartButton}
                    >
                        ¡Sí, avísame!
                    </a>
                ) : status === 'done' ? (
                    <span className={styles.addToCartButton} aria-live="polite">Suscrito, gracias</span>
                ) : (
                    <form onSubmit={handleNewsletterSubmit} style={{ display: 'flex', gap: 8, width: '100%', gridColumn: '1 / -1' }}>
                        <input
                            type="email"
                            required
                            placeholder="tucorreo@ejemplo.com"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            aria-label="Tu correo"
                            style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 8, border: '1px solid #d8dde3' }}
                        />
                        <button type="submit" className={styles.addToCartButton} disabled={status === 'sending'}>
                            {status === 'sending' ? '...' : '¡Sí, avísame!'}
                        </button>
                    </form>
                )}
            </div>
        </article>
    );
}

function InlineFeaturedCard({ deal }) {
    const price = Number(deal.price || 0);
    const originalPrice = Number(deal.originalPrice || 0);
    const detailDeal = {
        id: deal.id,
        name: deal.name,
        description: deal.description,
        image: deal.imageUrl,
        url: deal.affiliateUrl,
        clickRef: deal.clickRef,
        price,
        oldPrice: originalPrice,
        discount: deal.discount,
        store: deal.store,
        category: deal.category,
        listingOrigin: deal.listingOrigin || (deal.isSponsored ? 'seller' : 'affiliate')
    };

    return (
        <article className={`${styles.productItem} ${styles.inlineFeaturedItem}`} aria-label={`${deal.isSponsored ? 'Anuncio' : 'Oferta destacada'}: ${deal.name}`}>
            <div className={styles.inlineFeaturedHeading}>
                <span className={deal.isSponsored ? styles.inlineAdBadge : styles.inlineHookBadge}>
                    {deal.isSponsored ? 'Anuncio' : 'Top descuento'}
                </span>
                <span>{deal.isSponsored ? `Anuncio pagado · Puesto nº${deal.position}` : 'Elegido por Esganga'}</span>
            </div>
            <Link to={`/ofertas/${deal.id}`} state={{ deal: detailDeal }} className={styles.imageWrap}>
                {deal.imageUrl && (
                    <SensitiveThumbnail
                        isSensitive={slugify(deal.category) === 'erotico-adultos'}
                        src={withShopifyWidth(deal.imageUrl, 500)}
                        alt={deal.name}
                        className={styles.productImage}
                        loading="lazy"
                        decoding="async"
                    />
                )}
            </Link>
            <div className={styles.productMeta}>
                <span>{deal.store}</span>
                <span>{formatCategory(deal.category)}</span>
            </div>
            <h3 className={styles.productName}>
                <Link to={`/ofertas/${deal.id}`} state={{ deal: detailDeal }}>{deal.name}</Link>
            </h3>
            <p className={styles.productDescription}>{deal.description || 'Producto destacado para revisar precio, condiciones y tienda.'}</p>
            <div className={styles.priceRow}>
                <p className={styles.productPrice}>{price.toFixed(2)}€</p>
                {originalPrice > price && <p className={styles.oldPrice}>{originalPrice.toFixed(2)}€</p>}
            </div>
            <p className={styles.inlineFeaturedDisclosure}>
                {deal.isSponsored ? 'Posicion contratada por el vendedor.' : 'Este puesto todavia no ha sido contratado.'}
            </p>
            <div className={styles.actions}>
                <Link to={`/ofertas/${deal.id}`} state={{ deal: detailDeal }} className={styles.addToCartButton}>
                    {deal.isSponsored ? 'Ver anuncio' : 'Ver oferta'}
                </Link>
            </div>
        </article>
    );
}

// Bug real 2026-08-11: DealLanding pasaba item.name ("Erotico y adultos
// (+18)") en vez de item.slug -- slugify(nombre) no siempre coincide con el
// slug real (funcionaba por casualidad para nombres simples tipo "Moda", no
// para nombres con conectores/parentesis/digitos). Ya corregido en el
// llamante, pero se anade tambien busqueda por nombre aqui (mismo patron ya
// usado para tiendas en getNavigationStore/findStore) para que un futuro
// caller que vuelva a pasar el nombre en vez del slug no rompa el filtro en
// silencio.
function getCategorySlug(value) {
    const slug = slugify(value);
    if (!slug || slug === 'todos' || slug === 'todas' || slug === 'todas-las-categorias') {
        return '';
    }

    const bySlug = findCategory(slug);
    if (bySlug) {
        return bySlug.slug;
    }

    const byName = catalogCategories.find((category) => slugify(category.name) === slug);
    return byName?.slug || slug;
}

function mergeDeals(currentDeals, nextDeals) {
    const seen = new Set(currentDeals.map((deal) => String(deal.id)));
    const merged = [...currentDeals];

    for (const deal of nextDeals) {
        if (!seen.has(String(deal.id))) {
            seen.add(String(deal.id));
            merged.push(deal);
        }
    }

    return merged;
}

function StoreIcon({ name, logo }) {
    if (logo) {
        return <img src={logo} alt={name} className={styles.offerLogo} />;
    }

    const initial = String(name || '?').trim().charAt(0).toUpperCase() || '?';
    return <span className={styles.offerAvatar} aria-hidden="true">{initial}</span>;
}

function getDiscountNumber(deal) {
    if (typeof deal.discount === 'number') {
        return deal.discount;
    }

    const match = String(deal.discount || '').match(/\d+/);
    return match ? Number(match[0]) : 0;
}

function sortDeals(dealA, dealB, sortBy) {
    if (sortBy === 'relevance') {
        return 0;
    }

    if (sortBy === 'discount') {
        return getDiscountNumber(dealB) - getDiscountNumber(dealA) || Number(dealB.heat || 0) - Number(dealA.heat || 0);
    }

    if (sortBy === 'price') {
        return Number(dealA.price || Number.MAX_SAFE_INTEGER) - Number(dealB.price || Number.MAX_SAFE_INTEGER);
    }

    if (sortBy === 'recent') {
        return new Date(dealB.updatedAt || 0).getTime() - new Date(dealA.updatedAt || 0).getTime();
    }

    return Number(dealB.heat || 0) - Number(dealA.heat || 0);
}

function getSearchText(deal) {
    return slugify([deal.name, deal.description, deal.store, deal.category, deal.highlight].join(' '));
}

// "Actualizada hoy" (fijo, todo el dia) no transmitia que el catalogo se
// refresca de verdad cada 30 min (job 'feeds') -- un sitio de chollos vive
// de sentirse "vivo". deal.updatedAt ya existe (se usa para el orden
// "recientes"), solo faltaba mostrarlo de forma relativa.
function formatFreshness(updatedAt) {
    if (!updatedAt) {
        return 'Actualizada hoy';
    }

    const updatedMs = new Date(updatedAt).getTime();
    if (!Number.isFinite(updatedMs)) {
        return 'Actualizada hoy';
    }

    const minutes = Math.floor((Date.now() - updatedMs) / 60000);
    if (minutes < 1) {
        return 'Actualizada ahora mismo';
    }
    if (minutes < 60) {
        return `Actualizada hace ${minutes} min`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return `Actualizada hace ${hours}h`;
    }

    const days = Math.floor(hours / 24);
    if (days === 1) {
        return 'Actualizada ayer';
    }
    if (days < 7) {
        return `Actualizada hace ${days} dias`;
    }

    return `Actualizada el ${new Date(updatedMs).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}`;
}

function getConfidenceLabel(deal, saving) {
    if (Number(deal.heat || 0) >= 300) {
        return 'Selección Esganga';
    }

    if (saving > 0) {
        return 'Ahorro visible';
    }

    return 'Revisar precio final';
}

function formatCategory(value) {
    return String(value || '')
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatFacetValue(facet, value) {
    if (facet.type === 'number') {
        const suffixes = {
            storage_gb: 'GB',
            ram_gb: 'GB',
            screen_inches: '"',
            volume_ml: 'ml',
            capacity_l: 'L',
            power_w: 'W',
            weight_kg: 'kg'
        };
        return `${value}${suffixes[facet.key] || ''}`;
    }

    return value;
}

function trackClick(dealId, clickRef) {
    trackOutboundClick(dealId, 'web', clickRef);
}

// El fetch de resultados ya tiene su propio debounce de 300ms (responsivo a
// proposito, para que la lista se sienta rapida) -- pero registrar UNA
// entrada de analitica por cada fetch asentado a 300ms sigue generando un
// fragmento por cada pausa natural al escribir ("gafas", "gafas-de-so",
// "gafas-de-sol-hombre"...), ensuciando el informe de busquedas mas
// populares con variantes parciales de la misma busqueda. Un segundo
// debounce, mas largo y dedicado solo a la analitica (no al fetch), se
// encarga de quedarse solo con la ultima variante estable.
let searchEventTimer = null;

function recordSearchEvent(payload) {
    const hasSignal = payload.query ||
        payload.category ||
        payload.store ||
        payload.onlyHot ||
        Number(payload.minDiscount || 0) > 0 ||
        Number(payload.minPrice || 0) > 0 ||
        Number(payload.maxPrice || 0) > 0;
    if (!hasSignal) {
        return;
    }

    clearTimeout(searchEventTimer);
    searchEventTimer = setTimeout(() => {
        trackSearchEvent({ ...payload, source: 'product-list' });
    }, 1500);
}

export default ProductList;
